import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    Chip,
    Stack,
    Divider,
    useTheme,
    alpha,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Person as PersonIcon,
    Assignment as AssignmentIcon,
    Engineering as EngineeringIcon,
    Science as ScienceIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';

const SIGNATURE_ROLES = [
    { role: 'Production Supervisor', required: true, icon: <EngineeringIcon /> },
    { role: 'Quality Control', required: true, icon: <ScienceIcon /> },
    { role: 'Final Release', required: true, icon: <AdminPanelSettingsIcon /> },
    { role: 'Batch Operator', required: false, icon: <PersonIcon /> }
];

export default function SignatureLog({ signatures, setSignatures, batchId, onDataUpdate, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const { canAccessPage, canEditPage, canProvideProductionSupervisorSignature, canProvideQualityControlSignature, canProvideFinalReleaseSignature } = usePermissions();
    const [expandedRole, setExpandedRole] = useState(null);
    const [roleFormData, setRoleFormData] = useState({});

    // Initialize form data for each role
    React.useEffect(() => {
        const initialData = {};
        SIGNATURE_ROLES.forEach(role => {
            const existingSignature = signatures.find(s => s.role === role.role);
            initialData[role.role] = {
                signer_name: existingSignature?.signer_name || '',
                signer_title: existingSignature?.signer_title || '',
                signed_at: existingSignature?.signed_at ? dayjs(existingSignature.signed_at) : dayjs(),
                comments: existingSignature?.comments || ''
            };
        });
        setRoleFormData(initialData);
    }, [signatures]);

    const handleAccordionChange = (role) => (event, isExpanded) => {
        setExpandedRole(isExpanded ? role : null);
    };

    const updateRoleFormData = (role, field, value) => {
        setRoleFormData(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                [field]: value
            }
        }));
    };

    const handleSaveRoleSignature = async (role) => {
        const formData = roleFormData[role];
        if (!formData?.signer_name?.trim()) {
            console.error('Signer name is required');
            return;
        }

        if (!batchId) {
            console.error('Batch ID is required for signature');
            return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            console.error('User not authenticated');
            return;
        }

        const roleData = SIGNATURE_ROLES.find(r => r.role === role);
        const signatureData = {
            role: role,
            signer_name: formData.signer_name,
            signer_title: formData.signer_title,
            signed_at: formData.signed_at.toISOString(),
            comments: formData.comments,
            batch_id: batchId,
            typed: true,
            is_required: roleData?.required || false,
            user_id: user.id
        };

        try {
            const existingSignature = signatures.find(s => s.role === role);
            
            if (existingSignature) {
                const { data, error } = await supabase
                    .from('batch_signatures')
                    .update(signatureData)
                    .eq('id', existingSignature.id)
                    .select()
                    .single();

                if (error) {
                    console.error('Error updating signature:', error);
                    return;
                }
                
                setSignatures(prev => prev.map(s => s.id === existingSignature.id ? data : s));
            } else {
                const { data, error } = await supabase
                    .from('batch_signatures')
                    .insert(signatureData)
                    .select()
                    .single();
                
                if (error) {
                    console.error('Error inserting signature:', error);
                    return;
                }
                
                setSignatures(prev => [...prev, data]);
            }
            
            setExpandedRole(null);
            
            // Notify parent of data update
            if (onDataUpdate) {
                onDataUpdate();
            }
        } catch (error) {
            console.error('Error saving signature:', error);
        }
    };

    const getRoleIcon = (role) => {
        const roleData = SIGNATURE_ROLES.find(r => r.role === role);
        return roleData?.icon || <PersonIcon />;
    };

    const getRoleColor = (role) => {
        const colors = {
            'Production Supervisor': '#2563eb',
            'Quality Control': '#dc2626', 
            'Final Release': '#16a34a',
            'Batch Operator': '#ea580c'
        };
        return colors[role] || '#6b7280';
    };

    const signedRoles = signatures.map(s => s.role);
    const requiredRoles = SIGNATURE_ROLES.filter(r => r.required).map(r => r.role);
    const signedRequiredRoles = signedRoles.filter(role => requiredRoles.includes(role));
    const missingRoles = requiredRoles.filter(role => !signedRoles.includes(role));
    const completionPercentage = Math.round((signedRequiredRoles.length / requiredRoles.length) * 100);
    const isComplete = missingRoles.length === 0;
    const canEdit = canEditPage('batch_logs_signatures');

    // Permission check for batch logs signatures
    if (!canAccessPage('batch_logs_signatures')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                px: 3
            }}>
                <Card sx={{
                    p: 8,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 600,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 6,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <PersonIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        mb: 3, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2.5rem',
                        letterSpacing: '-0.8px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 4,
                        fontWeight: 500,
                        fontSize: '1.2rem'
                    }}>
                        Batch Logs Signatures Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        maxWidth: 450,
                        mx: 'auto'
                    }}>
                        You don't currently have the necessary permissions to access the Batch Logs Signatures. 
                        Contact your system administrator to request access or verify your role permissions.
                    </Typography>
                </Card>
            </Box>
        );
    }

    // Helper function to check if user can sign for specific role
    const canSignForRole = (role) => {
        switch (role) {
            case 'Production Supervisor':
                return canProvideProductionSupervisorSignature();
            case 'Quality Control':
                return canProvideQualityControlSignature();
            case 'Final Release':
                return canProvideFinalReleaseSignature();
            case 'Batch Operator':
                return canEdit; // General edit permission for batch operator
            default:
                return false;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Grid container spacing={3}>
                    {/* Summary Widgets */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            height: 180,
                            display: 'flex',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
                                <Typography variant="h1" sx={{ 
                                    fontWeight: 500, 
                                    color: theme.palette.primary.main,
                                    mb: 1,
                                    lineHeight: 1
                                }}>
                                    {signatures.length}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    SIGNATURES
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: isComplete ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            height: 180,
                            display: 'flex',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
                                <Typography variant="h1" sx={{ 
                                    fontWeight: 500, 
                                    color: isComplete ? theme.palette.success.main : theme.palette.warning.main,
                                    mb: 1,
                                    lineHeight: 1
                                }}>
                                    {completionPercentage}%
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    COMPLETION
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: missingRoles.length > 0 ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.success.main, 0.1),
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            height: 180,
                            display: 'flex',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
                                <Typography variant="h1" sx={{ 
                                    fontWeight: 500, 
                                    color: missingRoles.length > 0 ? theme.palette.error.main : theme.palette.success.main,
                                    mb: 1,
                                    lineHeight: 1
                                }}>
                                    {missingRoles.length}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    MISSING
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: isComplete ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[400], 0.1),
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            height: 180,
                            display: 'flex',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
                                <Typography variant="h2" sx={{ 
                                    fontWeight: 500, 
                                    color: isComplete ? theme.palette.success.main : theme.palette.grey[500],
                                    mb: 1,
                                    lineHeight: 1
                                }}>
                                    {isComplete ? 'Approved' : 'Pending'}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    STATUS
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Role Status Grid */}
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <PersonIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Signature Requirements
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Required approvals for batch release
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                {SIGNATURE_ROLES.map((role) => {
                                    const signature = signatures.find(s => s.role === role.role);
                                    const isSigned = !!signature;
                                    const formData = roleFormData[role.role] || {};
                                    
                                    return (
                                        <Box key={role.role} sx={{ mb: 2 }}>
                                            <Accordion 
                                                expanded={expandedRole === role.role}
                                                onChange={canSignForRole(role.role) ? handleAccordionChange(role.role) : undefined}
                                                disabled={!canSignForRole(role.role)}
                                                sx={{
                                                    border: `1px solid ${isSigned ? theme.palette.success.main : theme.palette.grey[400]}`,
                                                    borderRadius: 2,
                                                    backgroundColor: isSigned ? alpha(theme.palette.success.main, 0.05) : 'white',
                                                    '&:before': { display: 'none' },
                                                    boxShadow: 'none',
                                                    opacity: canSignForRole(role.role) ? 1 : 0.6
                                                }}
                                            >
                                                <AccordionSummary 
                                                    expandIcon={<ExpandMoreIcon />}
                                                    sx={{
                                                        '& .MuiAccordionSummary-content': { alignItems: 'center' },
                                                        borderRadius: 2
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                                                        <Box sx={{ color: isSigned ? theme.palette.success.main : theme.palette.text.secondary }}>
                                                            {role.icon}
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                {role.role}
                                                            </Typography>
                                                            {isSigned ? (
                                                                <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
                                                                    Signed by {signature.signer_name} • {dayjs(signature.signed_at).format(batchTimeFormat === '24hour' ? 'MMM DD, HH:mm' : 'MMM DD, h:mm A')}
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {canSignForRole(role.role) ? 'Click to sign' : 'No signing permission'}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Chip 
                                                            label={role.required ? 'Required' : 'Optional'}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: role.required ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.grey[400], 0.1),
                                                                color: role.required ? theme.palette.error.main : theme.palette.grey[600],
                                                                fontWeight: 500
                                                            }}
                                                        />
                                                        {isSigned && (
                                                            <CheckCircleIcon sx={{ color: theme.palette.success.main, ml: 1 }} />
                                                        )}
                                                    </Box>
                                                </AccordionSummary>
                                                
                                                <AccordionDetails sx={{ pt: 0 }}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label="Signer Name"
                                                                fullWidth
                                                                value={formData.signer_name || ''}
                                                                onChange={(e) => updateRoleFormData(role.role, 'signer_name', e.target.value)}
                                                                required
                                                                disabled={!canSignForRole(role.role)}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                                    }
                                                                }}
                                                            />
                                                        </Grid>
                                                        
                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                label="Title/Position"
                                                                fullWidth
                                                                value={formData.signer_title || ''}
                                                                onChange={(e) => updateRoleFormData(role.role, 'signer_title', e.target.value)}
                                                                placeholder="e.g., Production Manager, QC Analyst"
                                                                disabled={!canSignForRole(role.role)}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                                    }
                                                                }}
                                                            />
                                                        </Grid>
                                                        
                                                        <Grid item xs={12} md={6}>
                                                            <DateTimePicker
                                                                label="Signature Date & Time"
                                                                value={formData.signed_at || dayjs()}
                                                                onChange={(newValue) => updateRoleFormData(role.role, 'signed_at', newValue)}
                                                                disabled={!canSignForRole(role.role)}
                                                                format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                                                                ampm={batchTimeFormat !== '24hour'}
                                                                slotProps={{
                                                                    textField: {
                                                                        fullWidth: true,
                                                                        disabled: !canSignForRole(role.role),
                                                                        sx: {
                                                                            '& .MuiOutlinedInput-root': {
                                                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                                            }
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </Grid>
                                                        
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label="Comments (optional)"
                                                                multiline
                                                                rows={2}
                                                                fullWidth
                                                                value={formData.comments || ''}
                                                                onChange={(e) => updateRoleFormData(role.role, 'comments', e.target.value)}
                                                                placeholder="Additional notes or comments..."
                                                                disabled={!canSignForRole(role.role)}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                                    }
                                                                }}
                                                            />
                                                        </Grid>

                                                        {formData.signer_name && (
                                                            <Grid item xs={12}>
                                                                <Box sx={{ 
                                                                    p: 3, 
                                                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, 
                                                                    borderRadius: 2, 
                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                                                }}>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                                                        Signature Preview:
                                                                    </Typography>
                                                                    <Box sx={{ 
                                                                        fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                                                        fontSize: '28px', 
                                                                        color: theme.palette.text.primary,
                                                                        textAlign: 'center',
                                                                        textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
                                                                    }}>
                                                                        {formData.signer_name}
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                        )}
                                                        
                                                        <Grid item xs={12}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                                                <Button 
                                                                    onClick={() => setExpandedRole(null)}
                                                                    sx={{
                                                                        borderRadius: 2,
                                                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                                                        fontWeight: 500,
                                                                        px: 3,
                                                                        py: 1
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button 
                                                                    onClick={() => handleSaveRoleSignature(role.role)}
                                                                    variant="contained"
                                                                    disabled={!formData.signer_name?.trim() || !canSignForRole(role.role)}
                                                                    sx={{ 
                                                                        borderRadius: 2,
                                                                        px: 4,
                                                                        py: 1.5,
                                                                        color: theme.palette.text.secondary,
                                                                        fontWeight: 500,
                                                                        background: 'rgba(255, 255, 255, 0.95)',
                                                                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                                                        backdropFilter: 'blur(10px)',
                                                                        '&:hover': {
                                                                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                                                        },
                                                                        '&:disabled': {
                                                                            opacity: 0.6
                                                                        },
                                                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                                    }}
                                                                >
                                                                    {!canSignForRole(role.role) ? 'No Permission' : (isSigned ? 'Update' : 'Sign')}
                                                                </Button>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </AccordionDetails>
                                            </Accordion>
                                        </Box>
                                    );
                                })}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Signature Records */}
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Signature Records
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Documented approvals and authorizations
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                {signatures.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 8 }}>
                                        <PersonIcon sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 3 }} />
                                        <Typography variant="h5" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                            No Signatures Recorded
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Add signatures from authorized personnel to approve this batch
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {signatures.map((signature, index) => (
                                            <Box 
                                                key={signature.id}
                                                sx={{ 
                                                    border: `1px solid ${getRoleColor(signature.role)}`,
                                                    borderRadius: 4,
                                                    backgroundColor: alpha(getRoleColor(signature.role), 0.02),
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    '&:hover': { 
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: `0 8px 32px ${alpha(getRoleColor(signature.role), 0.25)}`,
                                                        backgroundColor: alpha(getRoleColor(signature.role), 0.05)
                                                    }
                                                }}
                                                onClick={() => setExpandedRole(signature.role)}
                                            >
                                                <Box sx={{ p: 4 }}>
                                                    <Grid container spacing={3} alignItems="center">
                                                        {/* Role Badge */}
                                                        <Grid item xs={12} md={2.5}>
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <Chip 
                                                                    label={signature.role}
                                                                    sx={{ 
                                                                        backgroundColor: getRoleColor(signature.role),
                                                                        color: 'white',
                                                                        fontWeight: 500,
                                                                        fontSize: '0.8rem',
                                                                        px: 2,
                                                                        py: 1,
                                                                        height: 'auto',
                                                                        borderRadius: 3,
                                                                        minWidth: 'fit-content',
                                                                        '& .MuiChip-label': {
                                                                            whiteSpace: 'nowrap'
                                                                        }
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Grid>

                                                        {/* Signer Info */}
                                                        <Grid item xs={12} md={2.5}>
                                                            <Box>
                                                                <Typography variant="h6" sx={{ 
                                                                    fontWeight: 500, 
                                                                    color: theme.palette.text.primary, 
                                                                    mb: 0.5,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {signature.signer_name}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" sx={{ 
                                                                    fontWeight: 500,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {signature.signer_title}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>

                                                        {/* Signature */}
                                                        <Grid item xs={12} md={4}>
                                                            <Box sx={{ 
                                                                textAlign: 'center',
                                                                py: 3,
                                                                px: 4,
                                                                backgroundColor: 'white',
                                                                border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                                                                borderRadius: 3,
                                                                boxShadow: `inset 0 2px 8px ${alpha(theme.palette.grey[900], 0.05)}`
                                                            }}>
                                                                <Box sx={{ 
                                                                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                                                    fontSize: '32px', 
                                                                    color: theme.palette.text.primary,
                                                                    mb: 1,
                                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                                                                    lineHeight: 1.2,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {signature.signer_name}
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ 
                                                                    fontWeight: 500,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: 1
                                                                }}>
                                                                    Digital Signature
                                                                </Typography>
                                                            </Box>
                                                        </Grid>

                                                        {/* Time & Comments */}
                                                        <Grid item xs={12} md={3}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 2 }}>
                                                                    Signed: {dayjs(signature.signed_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY' : 'MMM DD, YYYY')}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary, mb: 2 }}>
                                                                    Time: {dayjs(signature.signed_at).format(batchTimeFormat === '24hour' ? 'HH:mm' : 'h:mm A')}
                                                                </Typography>
                                                                {signature.comments && (
                                                                    <Box sx={{ 
                                                                        p: 2, 
                                                                        backgroundColor: alpha(theme.palette.grey[100], 0.8), 
                                                                        borderRadius: 2,
                                                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
                                                                    }}>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                                            Comments:
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ 
                                                                            fontWeight: 500, 
                                                                            lineHeight: 1.4,
                                                                            wordWrap: 'break-word',
                                                                            overflowWrap: 'break-word',
                                                                            wordBreak: 'break-word',
                                                                            hyphens: 'auto'
                                                                        }}>
                                                                            {signature.comments}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
        </Box>
        </LocalizationProvider>
    );
} 